import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  TrendingUp, ShoppingBag, Users, FileText, MessageSquare,
  ChevronRight, RefreshCw, LogOut, Package, CheckCircle,
  Clock, AlertCircle, Euro, Calendar, Mail, Phone
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { useAuth } from "../context/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ADMIN_EMAIL = "contact@atelierdumac.fr";

const fmt = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = (s) => s ? new Date(s).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";

function KpiCard({ icon: Icon, label, value, sub, color = "#FF5C00" }) {
  return (
    <div className="bg-[#111113] border border-white/8 rounded-2xl p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-bold uppercase tracking-widest text-white/30">{label}</span>
        <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: color + "18" }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
      </div>
      <p className="text-2xl font-black text-white">{value}</p>
      {sub && <p className="text-xs text-white/35 mt-1">{sub}</p>}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    paid: ["bg-[#34C759]/10 text-[#34C759]", "Payé"],
    pending: ["bg-yellow-500/10 text-yellow-400", "En attente"],
    initiated: ["bg-white/5 text-white/40", "Initié"],
    complete: ["bg-[#34C759]/10 text-[#34C759]", "Complet"],
  };
  const [cls, label] = map[status] || ["bg-white/5 text-white/30", status];
  return <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${cls}`}>{label}</span>;
}

function SectionHeader({ title, count, onRefresh, loading }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-black uppercase tracking-widest text-white/60">{title}</h2>
        {count !== undefined && (
          <span className="text-xs bg-white/5 text-white/40 px-2 py-0.5 rounded-full">{count}</span>
        )}
      </div>
      {onRefresh && (
        <button onClick={onRefresh} className={`p-1.5 text-white/30 hover:text-white transition-colors ${loading ? "animate-spin" : ""}`}>
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

export default function AdminPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [devis, setDevis] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    document.title = "Admin — Atelier du Mac";
    if (!user) { navigate("/connexion"); return; }
    if (user.email !== ADMIN_EMAIL) { navigate("/"); return; }
    loadAll();
  }, [user]);

  const apiFetch = useCallback(async (path) => {
    const res = await fetch(`${BACKEND}/api${path}`, { credentials: "include" });
    if (!res.ok) throw new Error(`Erreur ${res.status}`);
    return res.json();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, o, c, d, co] = await Promise.all([
        apiFetch("/admin/stats"),
        apiFetch("/admin/orders"),
        apiFetch("/admin/clients"),
        apiFetch("/admin/devis"),
        apiFetch("/admin/contacts"),
      ]);
      setStats(s);
      setOrders(o.orders || []);
      setClients(c.clients || []);
      setDevis(d.devis || []);
      setContacts(co.contacts || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    await loadAll();
    setRefreshing(false);
  };

  const handleLogout = async () => { await logout(); navigate("/"); };

  if (!user || user.email !== ADMIN_EMAIL) return null;

  const tabs = [
    { id: "dashboard", label: "Vue générale" },
    { id: "orders", label: `Commandes (${orders.length})` },
    { id: "devis", label: `Devis (${devis.length})` },
    { id: "contacts", label: `Messages (${contacts.length})` },
    { id: "clients", label: `Clients (${clients.length})` },
  ];

  return (
    <div className="bg-[#0A0A0C] min-h-screen">
      {/* Admin Header */}
      <header className="sticky top-0 z-30 bg-[#0A0A0C]/95 backdrop-blur-xl border-b border-white/8 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-xs text-white/30 hover:text-white/60 transition-colors flex items-center gap-1">
              <ChevronRight className="w-3 h-3 rotate-180" /> Site
            </Link>
            <span className="text-white/15">/</span>
            <span className="text-sm font-black text-white">Dashboard Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={refresh} disabled={refreshing}
              className={`flex items-center gap-1.5 text-xs text-white/40 hover:text-white border border-white/10 px-3 py-1.5 rounded-full transition-colors ${refreshing ? "opacity-50" : ""}`}>
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> Actualiser
            </button>
            <button onClick={handleLogout} className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white transition-colors">
              <LogOut className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-1 overflow-x-auto pb-1 mb-8 scrollbar-hide">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`flex-shrink-0 text-xs font-semibold px-4 py-2 rounded-full transition-all ${
                tab === t.id ? "bg-[#FF5C00] text-white" : "bg-white/5 text-white/40 hover:text-white hover:bg-white/10"
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 text-[#FF5C00] animate-spin" />
          </div>
        ) : (
          <>
            {/* DASHBOARD */}
            {tab === "dashboard" && stats && (
              <div className="space-y-8">
                {/* KPIs */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                  <KpiCard icon={Euro} label="CA Total" value={fmt(stats.total_revenue)} sub="Toutes commandes payées" />
                  <KpiCard icon={Calendar} label="CA ce mois" value={fmt(stats.monthly_revenue)} sub={`${stats.orders_month} commande(s)`} />
                  <KpiCard icon={TrendingUp} label="CA aujourd'hui" value={fmt(stats.daily_revenue)} sub={`${stats.orders_today} commande(s)`} />
                  <KpiCard icon={ShoppingBag} label="Commandes" value={stats.orders_total} sub="Total payées" color="#34C759" />
                  <KpiCard icon={Users} label="Clients" value={stats.clients_count} sub="Comptes créés" color="#007AFF" />
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-2 gap-4">
                  <KpiCard icon={FileText} label="Devis reçus" value={stats.devis_count} sub="Total demandes" color="#FF9F0A" />
                  <KpiCard icon={MessageSquare} label="Messages" value={stats.contacts_count} sub="Formulaire contact" color="#BF5AF2" />
                </div>

                {/* Revenue chart */}
                <div className="bg-[#111113] border border-white/8 rounded-2xl p-6">
                  <h3 className="text-sm font-black uppercase tracking-widest text-white/40 mb-6">
                    Chiffre d'affaires — 30 derniers jours
                  </h3>
                  {stats.revenue_chart?.some(d => d.revenue > 0) ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={stats.revenue_chart} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                        <XAxis dataKey="date" tick={{ fontSize: 10, fill: "rgba(255,255,255,0.3)" }}
                          tickLine={false} axisLine={false} interval={4} />
                        <YAxis tick={{ fontSize: 10, fill: "rgba(255,255,255,0.3)" }}
                          tickLine={false} axisLine={false} tickFormatter={v => `${v}€`} />
                        <Tooltip
                          contentStyle={{ background: "#1A1A1E", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, fontSize: 12 }}
                          labelStyle={{ color: "rgba(255,255,255,0.6)" }}
                          formatter={v => [`${v}€`, "CA"]}
                        />
                        <Bar dataKey="revenue" fill="#FF5C00" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-32 text-white/20 text-sm">
                      Aucune vente sur les 30 derniers jours
                    </div>
                  )}
                </div>

                {/* Recent orders */}
                <div className="bg-[#111113] border border-white/8 rounded-2xl p-6">
                  <SectionHeader title="Dernières commandes" count={orders.length} />
                  <OrdersTable orders={orders.slice(0, 5)} />
                  {orders.length > 5 && (
                    <button onClick={() => setTab("orders")} className="mt-4 text-xs text-[#FF5C00] hover:underline">
                      Voir toutes les commandes ({orders.length}) →
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* ORDERS */}
            {tab === "orders" && (
              <div className="bg-[#111113] border border-white/8 rounded-2xl p-6">
                <SectionHeader title="Commandes" count={orders.length} onRefresh={refresh} loading={refreshing} />
                <OrdersTable orders={orders} />
              </div>
            )}

            {/* DEVIS */}
            {tab === "devis" && (
              <div className="bg-[#111113] border border-white/8 rounded-2xl p-6">
                <SectionHeader title="Demandes de devis" count={devis.length} onRefresh={refresh} loading={refreshing} />
                <div className="space-y-3">
                  {devis.length === 0 && <Empty label="Aucun devis" />}
                  {devis.map((d, i) => (
                    <div key={i} data-testid={`devis-row-${i}`}
                      className="p-4 rounded-xl bg-[#0A0A0C] border border-white/5 grid sm:grid-cols-4 gap-3">
                      <div>
                        <p className="text-xs text-white/30 mb-0.5">Client</p>
                        <p className="text-sm font-semibold text-white">{d.nom || "—"}</p>
                        <p className="text-xs text-white/40">{d.email}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/30 mb-0.5">Appareil</p>
                        <p className="text-sm text-white">{d.appareil} {d.modele && `— ${d.modele}`}</p>
                        <p className="text-xs text-[#FF5C00]">{d.type_reparation}</p>
                      </div>
                      <div className="sm:col-span-2">
                        <p className="text-xs text-white/30 mb-0.5">Description</p>
                        <p className="text-xs text-white/60 line-clamp-2">{d.description || "—"}</p>
                        <p className="text-xs text-white/25 mt-1">{fmtDate(d.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CONTACTS */}
            {tab === "contacts" && (
              <div className="bg-[#111113] border border-white/8 rounded-2xl p-6">
                <SectionHeader title="Messages reçus" count={contacts.length} onRefresh={refresh} loading={refreshing} />
                <div className="space-y-3">
                  {contacts.length === 0 && <Empty label="Aucun message" />}
                  {contacts.map((c, i) => (
                    <div key={i} data-testid={`contact-row-${i}`}
                      className="p-4 rounded-xl bg-[#0A0A0C] border border-white/5 grid sm:grid-cols-4 gap-3">
                      <div>
                        <p className="text-xs text-white/30 mb-0.5">Expéditeur</p>
                        <p className="text-sm font-semibold text-white">{c.nom}</p>
                        <a href={`mailto:${c.email}`} className="text-xs text-[#FF5C00] hover:underline flex items-center gap-1 mt-0.5">
                          <Mail className="w-3 h-3" />{c.email}
                        </a>
                        {c.telephone && (
                          <a href={`tel:${c.telephone}`} className="text-xs text-white/40 hover:text-white flex items-center gap-1 mt-0.5">
                            <Phone className="w-3 h-3" />{c.telephone}
                          </a>
                        )}
                      </div>
                      <div>
                        <p className="text-xs text-white/30 mb-0.5">Appareil</p>
                        <p className="text-sm text-white">{c.appareil}</p>
                        <span className="text-xs bg-white/5 text-white/40 px-2 py-0.5 rounded-full mt-1 inline-block">
                          {c.type_demande === "rdv" ? "Rendez-vous" : "Contact"}
                        </span>
                      </div>
                      <div className="sm:col-span-2">
                        <p className="text-xs text-white/30 mb-0.5">Message</p>
                        <p className="text-xs text-white/60 line-clamp-3">{c.message}</p>
                        <p className="text-xs text-white/25 mt-1">{fmtDate(c.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CLIENTS */}
            {tab === "clients" && (
              <div className="bg-[#111113] border border-white/8 rounded-2xl p-6">
                <SectionHeader title="Clients inscrits" count={clients.length} onRefresh={refresh} loading={refreshing} />
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/8">
                        {["Nom", "Email", "Téléphone", "Adresse", "Inscrit le", "Via"].map(h => (
                          <th key={h} className="text-left py-3 px-4 text-xs font-bold uppercase tracking-widest text-white/25">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {clients.length === 0 && (
                        <tr><td colSpan={6} className="py-12 text-center text-white/25 text-sm">Aucun client</td></tr>
                      )}
                      {clients.map((c, i) => (
                        <tr key={i} data-testid={`client-row-${i}`}
                          className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                          <td className="py-3.5 px-4 font-semibold text-white">{c.prenom} {c.nom}</td>
                          <td className="py-3.5 px-4 text-white/60">{c.email}</td>
                          <td className="py-3.5 px-4 text-white/50">{c.telephone || "—"}</td>
                          <td className="py-3.5 px-4 text-white/40 max-w-[160px] truncate">{c.adresse_postale || "—"}</td>
                          <td className="py-3.5 px-4 text-white/35 text-xs">{fmtDate(c.created_at)}</td>
                          <td className="py-3.5 px-4">
                            <span className={`text-xs px-2 py-1 rounded-full ${c.provider === "google" ? "bg-[#007AFF]/10 text-[#007AFF]" : "bg-white/5 text-white/30"}`}>
                              {c.provider === "google" ? "Google" : "Email"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function OrdersTable({ orders }) {
  if (orders.length === 0) return <Empty label="Aucune commande" />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/8">
            {["Date", "Client", "Articles", "Total", "Statut"].map(h => (
              <th key={h} className="text-left py-3 px-4 text-xs font-bold uppercase tracking-widest text-white/25">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {orders.map((o, i) => (
            <tr key={i} data-testid={`order-row-${i}`}
              className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
              <td className="py-3.5 px-4 text-white/40 text-xs whitespace-nowrap">{fmtDate(o.created_at)}</td>
              <td className="py-3.5 px-4 text-white/60">{o.customer_email || "—"}</td>
              <td className="py-3.5 px-4 text-white/50 max-w-[200px]">
                {o.items?.slice(0, 2).map((it, j) => (
                  <p key={j} className="text-xs truncate">{it.name}</p>
                ))}
                {o.items?.length > 2 && <p className="text-xs text-white/25">+{o.items.length - 2} autre(s)</p>}
              </td>
              <td className="py-3.5 px-4 font-black text-[#FF5C00]">{o.amount_total}€</td>
              <td className="py-3.5 px-4"><StatusBadge status={o.payment_status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Empty({ label }) {
  return (
    <div className="flex items-center justify-center py-16 text-white/20 text-sm">
      <Package className="w-5 h-5 mr-2" /> {label}
    </div>
  );
}
